<script lang="ts">
  import {
    Accordion,
    AccordionItem,
    FileDropzone,
    getModalStore,
    getToastStore,
  } from '@skeletonlabs/skeleton'
  import camelcaseKeys from 'camelcase-keys'
  import { onMount, type SvelteComponent } from 'svelte'

  import CloseModalButton from '@components/CloseModalButton.svelte'
  import {
    LabelingType,
    ModalityType,
    objectOrSegmentLabelingTypes,
  } from '@routes/data-qa/datasets/datasets-store.svelte'
  import { setRestoreState } from '@utils/modal-restore'
  import {
    checkIfObjectDetection,
    type ClassificationIfc,
    ClassificationRunArgs,
    DEFAULT_IMAGE_SIZE,
    DEFAULT_RADAR_SIZE,
    ObjectDetectionRunArgs,
  } from '@utils/run-args.svelte'

  import Icon from '$lib/components/Icon.svelte'

  interface Props {
    /** Exposes parent props to this component. */
    parent: SvelteComponent
  }

  type AccordionEvent = CustomEvent<{
    event?: Event
    id: string
    panelId: string
    open: boolean
    autocollapse: boolean
  }>

  let { parent }: Props = $props()

  let useAdvancedRunOptions = $state(false)
  let fileImportMode = $state(false)
  let customImageSize = $state(false)
  // Add support for segmentation
  let runArgs = $state<
    ((ClassificationRunArgs | ObjectDetectionRunArgs) & ClassificationIfc) | null
  >(null)

  const modalStore = getModalStore()
  const restoreKey: string | undefined = $modalStore[0]?.meta?.restoreKey
  const labelingType = $derived($modalStore[0].value.labelingType)
  const modality = $derived($modalStore[0].value.modality)
  const modalitiesWithoutAdvancedRunOptions = new Set([
    ModalityType.SPEECH,
    ModalityType.TABULAR,
    ModalityType.TIMESERIES,
    ModalityType.MULTIMODAL,
  ])

  const setRunArgs = () => {
    if (objectOrSegmentLabelingTypes.includes(labelingType)) {
      runArgs = new ObjectDetectionRunArgs()
    } else {
      runArgs = new ClassificationRunArgs()
    }
  }

  $effect(() => {
    setRunArgs()
  })

  const cannotUseAdvancedRunOptions = $derived(modalitiesWithoutAdvancedRunOptions.has(modality))

  const toastStore = getToastStore()
  let activeToast: string | null = null

  type ClassificationArgsJSON = ReturnType<typeof ClassificationRunArgs.prototype.toJSON>
  type ObjectDetectionArgsJSON = ReturnType<typeof ObjectDetectionRunArgs.prototype.toJSON>

  interface RunQARestoreState {
    useAdvancedRunOptions: boolean
    fileImportMode: boolean
    customImageSize: boolean
    runArgs:
      | { kind: 'classification'; data: ClassificationArgsJSON }
      | { kind: 'objectDetection'; data: ObjectDetectionArgsJSON }
      | null
  }
  const isRunQARestoreState = (v: unknown): v is RunQARestoreState =>
    !!v && typeof v === 'object' && 'useAdvancedRunOptions' in (v as Record<string, unknown>)

  const restoreRunArgs = (
    saved:
      | { kind: 'classification'; data: ClassificationArgsJSON }
      | { kind: 'objectDetection'; data: ObjectDetectionArgsJSON },
  ) => {
    if (!saved) {
      return
    }
    if (saved.kind === 'objectDetection') {
      const newRun = new ObjectDetectionRunArgs()
      for (const key of ObjectDetectionRunArgs.keys()) {
        const val = (saved.data as Record<string, unknown>)[key]
        if (val !== undefined) {
          newRun.setField(
            key as unknown as keyof (typeof newRun)['defaultValues'],
            val as unknown as (typeof newRun)['defaultValues'][typeof key],
          )
        }
      }
      runArgs = newRun
    } else {
      const newRun = new ClassificationRunArgs()
      for (const key of ClassificationRunArgs.keys()) {
        const val = (saved.data as Record<string, unknown>)[key]
        if (val !== undefined) {
          newRun.setField(
            key as unknown as keyof (typeof newRun)['defaultValues'],
            val as unknown as (typeof newRun)['defaultValues'][typeof key],
          )
        }
      }
      runArgs = newRun
    }
  }

  // Restore previously cached state if present
  onMount(() => {
    if (
      $modalStore[0]?.meta?.restoreState &&
      isRunQARestoreState($modalStore[0].meta.restoreState)
    ) {
      const restoreState = $modalStore[0].meta.restoreState
      useAdvancedRunOptions = restoreState.useAdvancedRunOptions
      fileImportMode = restoreState.fileImportMode
      customImageSize = restoreState.customImageSize
      if (restoreState.runArgs) {
        restoreRunArgs(restoreState.runArgs)
      }
    }
  })

  $effect(() => {
    if (!restoreKey) {
      return
    }
    const args = runArgs
      ? runArgs instanceof ObjectDetectionRunArgs
        ? { kind: 'objectDetection' as const, data: runArgs.toJSON() }
        : { kind: 'classification' as const, data: (runArgs as ClassificationRunArgs).toJSON() }
      : null
    setRestoreState(restoreKey, {
      useAdvancedRunOptions,
      fileImportMode,
      customImageSize,
      runArgs: args,
    } satisfies RunQARestoreState)
  })

  const onFormCancel = () => {
    if ($modalStore[0].response) {
      $modalStore[0].response(false)
    }
    modalStore.close()
  }
  const onFormSubmit = () => {
    if (activeToast) {
      toastStore.close(activeToast)
    }

    if ($modalStore[0].response) {
      $modalStore[0].response(
        useAdvancedRunOptions && !cannotUseAdvancedRunOptions ? $state.snapshot(runArgs) : true,
      )
    }
    modalStore.close()
  }

  const accordionChange = (event: AccordionEvent) => {
    if (useAdvancedRunOptions) {
      setRunArgs()
    } // ⬆️ Reset the run args when the accordion is closed
    useAdvancedRunOptions = event.detail.open
  }

  const importFromFiles = () => {
    fileImportMode = true
  }

  const handleFileImport = (event: Event & { currentTarget: HTMLInputElement }) => {
    const file = event.currentTarget.files?.[0]
    if (file) {
      if (file.type !== 'application/json') {
        console.error('File is not a JSON file')
        toastStore.trigger({
          message:
            'File is not a JSON file. Please make sure that you are uploading the correct file.',
          background: 'variant-filled-error',
        })
        return
      }
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const json = camelcaseKeys(JSON.parse(e.target?.result as string), { deep: true })
          // ⬆️ Note: the cast to string is safe since we are using `reader.readAsText`
          let newRunArgs: ClassificationRunArgs | ObjectDetectionRunArgs
          if (checkIfObjectDetection(json.runArgs)) {
            newRunArgs = new ObjectDetectionRunArgs()
            const runArgKeys = ObjectDetectionRunArgs.keys()
            for (const key of runArgKeys) {
              if (ObjectDetectionRunArgs.nullMeaningfulKeys().includes(key)) {
                newRunArgs.setField(
                  key,
                  json.runArgs[key] === null ? null : Number(json.runArgs[key]),
                )
              } else if (json.runArgs[key] !== null) {
                newRunArgs.setField(key, json.runArgs[key])
              }
            }
          } else {
            newRunArgs = new ClassificationRunArgs()
            const runArgKeys = ClassificationRunArgs.keys()
            for (const key of runArgKeys) {
              if (json.runArgs[key] !== null) {
                newRunArgs.setField(key, json.runArgs[key])
              }
            }
          }
          if (newRunArgs.changedFields.size > 0) {
            runArgs = newRunArgs
            useAdvancedRunOptions = true
          } else {
            toastStore.trigger({
              message: 'No advanced run options were set in this JSON file',
              background: 'variant-filled-error',
            })
          }
          // You can now use the parsed JSON as needed
          fileImportMode = false
        } catch (err) {
          console.error('Failed to parse file:', err)
          toastStore.trigger({
            message: 'Failed to parse JSON file',
            background: 'variant-filled-error',
          })
        }
      }
      reader.readAsText(file)
    }
  }
</script>

{#if fileImportMode}
  <div class="card w-modal max-h-[90vh] space-y-4 overflow-y-scroll px-6 shadow-xl" role="dialog">
    <header
      class="sticky top-0 z-10 mt-1 justify-between bg-surface-50 p-4 text-2xl dark:bg-surface-800"
    >
      Import dataset QA run options from file
      <CloseModalButton onClose={parent.onClose} />
    </header>
    <article class="space-y-4 px-4">
      <form class="space-y-4">
        <FileDropzone name="fileImport" onchange={handleFileImport}>
          {#snippet lead()}
            <Icon icon="iconoir:upload-square-solid" class="h-12 w-12 justify-self-center" />
          {/snippet}
          {#snippet message()}
            <p>Drop files here or click to upload</p>
          {/snippet}
          {#snippet meta()}
            Please use the JSON file from the ZIP of a previous run's results
          {/snippet}
        </FileDropzone>
      </form>
    </article>
    <footer
      class="modal-footer sticky bottom-0 bg-surface-50 pb-2 pt-2 dark:bg-surface-800 {parent.regionFooter}"
    >
      <div class="flex w-full items-center justify-between">
        <button
          class="variant-filled-surface btn"
          type="button"
          onclick={() => {
            fileImportMode = false
          }}
        >
          Cancel import
        </button>
      </div>
    </footer>
  </div>
{:else if $modalStore[0]}
  <div class="card w-modal max-h-[90vh] space-y-4 overflow-y-scroll px-6 shadow-xl" role="dialog">
    <header
      class="sticky top-0 z-10 mt-1 justify-between bg-surface-50 p-4 text-2xl dark:bg-surface-800"
    >
      {$modalStore[0].title}
      <CloseModalButton onClose={parent.onClose} />
    </header>
    <article class="space-y-4 px-4">
      {$modalStore[0].body}
      <Accordion class="card variant-glass-surface mt-4">
        <AccordionItem on:toggle={accordionChange} open={useAdvancedRunOptions}>
          {#snippet lead()}
            <Icon icon="iconoir:settings" class="h-6 w-6" />
          {/snippet}
          {#snippet summary()}
            <h3>Advanced run options</h3>
          {/snippet}
          {#snippet content()}
            <form class="space-y-4">
              {#if cannotUseAdvancedRunOptions}
                {#if modality === ModalityType.TABULAR || modality === ModalityType.TIMESERIES}
                  <p>
                    No advanced options are available for tabular or time series datasets currently
                  </p>
                {:else if modality === ModalityType.MULTIMODAL}
                  <p>No advanced options are available for multimodal datasets currently</p>
                {:else}
                  <p>No advanced options are available for Speech datasets currently</p>
                {/if}
              {:else if runArgs}
                <div class="grid grid-cols-[auto,1fr] gap-2">
                  <input
                    id="upsample"
                    name="upsample"
                    type="checkbox"
                    class="checkbox my-0"
                    checked={runArgs.getField('upsample')}
                    onchange={(event) => {
                      runArgs?.setField('upsample', event.currentTarget.checked)
                    }}
                  />
                  <label class="label" for="upsample">Oversample under represented classes</label>
                </div>
              {/if}
              {#if runArgs instanceof ObjectDetectionRunArgs}
                <div class="columns-2">
                  <div>
                    <label class="label" for="minAbsBboxSize">Minimum absolute bbox size</label>
                    <input
                      id="minAbsBboxSize"
                      name="minAbsBboxSize"
                      type="number"
                      class="input my-0 box-border flex w-fit px-2 py-1"
                      step={1}
                      value={runArgs.getField('minAbsBboxSize')}
                      onchange={(event) => {
                        if (runArgs instanceof ObjectDetectionRunArgs) {
                          runArgs.setField('minAbsBboxSize', Number(event.currentTarget.value))
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label class="label" for="minAbsBboxArea">Minimum absolute bbox area</label>
                    <input
                      id="minAbsBboxArea"
                      name="minAbsBboxArea"
                      type="number"
                      class="input my-0 box-border flex w-fit px-2 py-1"
                      step={1}
                      value={runArgs.getField('minAbsBboxArea')}
                      onchange={(event) => {
                        if (runArgs instanceof ObjectDetectionRunArgs) {
                          runArgs.setField('minAbsBboxArea', Number(event.currentTarget.value))
                        }
                      }}
                    />
                  </div>
                </div>
                <div class="columns-2">
                  <div>
                    <label class="label" for="minRelBboxSize">Minimum relative bbox size</label>
                    <input
                      id="minRelBboxSize"
                      name="minRelBboxSize"
                      type="number"
                      class="input my-0 box-border flex w-fit px-2 py-1"
                      step={0.01}
                      value={runArgs.getField('minRelBboxSize')}
                      onchange={(event) => {
                        if (runArgs instanceof ObjectDetectionRunArgs) {
                          runArgs.setField('minRelBboxSize', Number(event.currentTarget.value))
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label class="label" for="minRelBboxArea">Minimum relative bbox area</label>
                    <input
                      id="minRelBboxArea"
                      name="minRelBboxArea"
                      type="number"
                      class="input my-0 box-border flex w-fit px-2 py-1"
                      step={0.01}
                      value={runArgs.getField('minRelBboxArea')}
                      onchange={(event) => {
                        if (runArgs instanceof ObjectDetectionRunArgs) {
                          runArgs.setField('minRelBboxArea', Number(event.currentTarget.value))
                        }
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div class="grid grid-cols-[auto,1fr] gap-2">
                    <input
                      id="cropFrame"
                      name="cropFrame"
                      type="checkbox"
                      class="checkbox my-0"
                      onchange={(event) => {
                        if (runArgs instanceof ObjectDetectionRunArgs) {
                          if (event.currentTarget.checked) {
                            runArgs.setField('cropRatio', null)
                          } else {
                            runArgs.setField('cropRatio', 1.0)
                          }
                        }
                      }}
                    />
                    <label class="label" for="cropFrame">Do not crop the frame</label>
                  </div>
                  {#if runArgs.getField('cropRatio') !== null}
                    <label class="label" for="cropRatio">Crop ratio</label>
                    <input
                      id="cropRatio"
                      name="cropRatio"
                      type="number"
                      class="input my-0 box-border flex w-fit min-w-96 px-2 py-1"
                      step={0.01}
                      min={1}
                      value={runArgs.getField('cropRatio')}
                      onchange={(event) => {
                        if (runArgs instanceof ObjectDetectionRunArgs) {
                          runArgs.setField('cropRatio', Number(event.currentTarget.value))
                        }
                      }}
                    />
                  {/if}
                  <aside class="alert variant-filled-surface m-2">
                    <div class="alert-message">
                      <p>Change this value at your own risk.</p>
                      <p>It is recommended to keep it at 1.0 unless you know what you are doing</p>
                    </div>
                  </aside>
                </div>
                <div>
                  <div class="grid grid-cols-[auto,1fr] gap-2">
                    <input
                      id="addMaskChannel"
                      name="addMaskChannel"
                      type="checkbox"
                      class="checkbox my-0"
                      checked={runArgs.getField('addMaskChannel')}
                      onchange={(event) => {
                        if (runArgs instanceof ObjectDetectionRunArgs) {
                          runArgs.setField('addMaskChannel', event.currentTarget.checked)
                        }
                      }}
                    />
                    <label class="label" for="addMaskChannel">Add mask channel</label>
                  </div>
                  <aside class="alert variant-filled-surface m-2">
                    <div class="alert-message">
                      <p>Change at your own risk.</p>
                      <p>
                        It is recommended to keep it at un-checked unless you know what you are
                        doing
                      </p>
                      <p>If the crop ratio is greater than 1.0, it is recommended to check this.</p>
                    </div>
                  </aside>
                </div>
              {/if}
              {#if !cannotUseAdvancedRunOptions && runArgs}
                <div>
                  <div class="grid grid-cols-[auto,1fr] gap-2">
                    <input
                      id="customImageSize"
                      name="customImageSize"
                      type="checkbox"
                      class="checkbox my-0"
                      checked={customImageSize}
                      onchange={(event) => {
                        customImageSize = event.currentTarget.checked
                        if (runArgs && customImageSize) {
                          runArgs.setField(
                            'imgSize',
                            modality === ModalityType.RADAR
                              ? DEFAULT_RADAR_SIZE
                              : DEFAULT_IMAGE_SIZE,
                          )
                        } else if (runArgs) {
                          runArgs.setField('imgSize', null)
                        }
                      }}
                    />
                    <label class="label" for="customImageSize"
                      >Input custom {labelingType === LabelingType.SingleLabelClassification
                        ? 'image'
                        : 'patch'} size</label
                    >
                  </div>
                  {#if customImageSize}
                    <div class="columns-2">
                      <div>
                        <label class="label" for="imgWidth">Image width</label>
                        <input
                          id="imgWidth"
                          name="imgWidth"
                          type="number"
                          class="input my-0 box-border flex w-fit px-2 py-1"
                          step={1}
                          value={runArgs.getField('imgSize')?.[0] ??
                            (modality === ModalityType.RADAR
                              ? DEFAULT_RADAR_SIZE[0]
                              : DEFAULT_IMAGE_SIZE[0])}
                          onchange={(event) => {
                            if (
                              runArgs instanceof ClassificationRunArgs ||
                              runArgs instanceof ObjectDetectionRunArgs
                            ) {
                              runArgs.setField('imgSize', [
                                Number(event.currentTarget.value),
                                runArgs.getField('imgSize')?.[1] ??
                                  (modality === ModalityType.RADAR
                                    ? DEFAULT_RADAR_SIZE[1]
                                    : DEFAULT_IMAGE_SIZE[1]),
                              ])
                            }
                          }}
                        />
                      </div>
                      <div>
                        <label class="label" for="imgHeight">Image height</label>
                        <input
                          id="imgHeight"
                          name="imgHeight"
                          type="number"
                          class="input my-0 box-border flex w-fit px-2 py-1"
                          step={1}
                          value={runArgs.getField('imgSize')?.[1] ??
                            (modality === ModalityType.RADAR
                              ? DEFAULT_RADAR_SIZE[1]
                              : DEFAULT_IMAGE_SIZE[1])}
                          onchange={(event) => {
                            if (
                              runArgs instanceof ClassificationRunArgs ||
                              runArgs instanceof ObjectDetectionRunArgs
                            ) {
                              runArgs.setField('imgSize', [
                                runArgs.getField('imgSize')?.[0] ??
                                  (modality === ModalityType.RADAR
                                    ? DEFAULT_RADAR_SIZE[0]
                                    : DEFAULT_IMAGE_SIZE[0]),
                                Number(event.currentTarget.value),
                              ])
                            }
                          }}
                        />
                      </div>
                    </div>
                    {#if modality !== ModalityType.RADAR}
                      <aside class="alert variant-filled-surface m-2">
                        <div class="alert-message">
                          <p>
                            It is recommended to keep the image size at 224 for width and 224 for
                            height.
                          </p>
                          <p>
                            If instances of a given class are known to be small relative to the
                            whole image, you might want to consider increasing the image size.
                          </p>
                          <p>
                            Mostly relevant for classification problems or when the crop ratio is
                            not 1.0
                          </p>
                        </div>
                      </aside>
                    {/if}
                  {/if}
                </div>
              {/if}
            </form>
          {/snippet}
        </AccordionItem>
      </Accordion>
    </article>
    <footer
      class="modal-footer sticky bottom-0 bg-surface-50 pb-4 pt-2 dark:bg-surface-800 {parent.regionFooter}"
    >
      <button class="variant-filled-secondary btn mr-auto" type="button" onclick={importFromFiles}>
        Import from file
      </button>
      <div class="variant-filled-primary btn-group w-min">
        <button class={parent.buttonNeutral} onclick={onFormCancel}
          >{parent.buttonTextCancel}</button
        >
        <button class={parent.buttonPositive} onclick={onFormSubmit}
          >{parent.buttonTextConfirm}</button
        >
      </div>
    </footer>
  </div>
{/if}
